import React, { useState, useEffect, useLayoutEffect, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';

import { gray2gradient, downloadCanvas, pointInConcavePolygon } from '../algorithms/geometry.js';
import Materials from '../algorithms/materials.js';
import { tic, toc } from '../algorithms/utils.js';

const cvSelected = document.createElement('canvas');

const useMaterials = ({
  canvasPool,
  imgSrc,
  dsmTexture,
  month,
  setShadingTexture,
  sceneRef,
  fillPolygon,
  updateLoadingStatus,
  proposalShadings,
  iframeCallback,
  onShadingLoaded,
  // External dependencies (passed from parent component like Canvas3D)
  lead = {},
  user = {},
  saveShadingsAPI = { create: () => Promise.resolve() }
}) => {
  const handleSaveShadings = async (gradients) => {
    const { uid } = lead;
    const blob = new Blob([JSON.stringify(gradients)]);
    const formData = new FormData();
    formData.append('shadings', blob, 'shadings.json');
    formData.append('uid', uid);
    console.log('Shading file uploading started!');
    await saveShadingsAPI.create(formData);
    console.log('Shading file uploading completed!');
    if (iframeCallback) {
      iframeCallback('Shading', 'Shading file upload completed');
    }
  };

  const [{ rgbLoadStatus, shadingLoadStatus }, setLoadStatus] = useState({
    rgbLoadStatus: 'RGB.Loading',
    shadingLoadStatus: 'Shading.Loading'
  });

  const [rgbTexture, setRGBTexture] = useState();
  const [texture, setTexture] = useState();
  const [selectedTexture, setSelectedTexture] = useState();
  const [materials] = useState(new Materials(canvasPool));
  const shadingLoadPromiseRef = useRef(Promise.resolve(null));

  useEffect(() => {
    materials.canvasPool = canvasPool;
    materials.fillPolygon = fillPolygon;
  }, [materials, canvasPool, fillPolygon]);

  const changeLoadStatus = (status) => {
    if (status.startsWith('RGB.')) setLoadStatus({ rgbLoadStatus: status, shadingLoadStatus });
    else if (status.startsWith('Shading.')) setLoadStatus({ rgbLoadStatus, shadingLoadStatus: status });
    updateLoadingStatus(status);
  };

  const changeMaterials = (scene) => {
    if (scene) {
      scene.traverse((node) => {
        if (node.material) {
          node.material.needsUpdate = true;
        }
      });
    }
  };

  const getSelectedTexture = (currentTexture) => {
    if (!currentTexture || !currentTexture.image || typeof currentTexture.image == 'object') return null;
    const sz = currentTexture.image.width;
    cvSelected.width = currentTexture.image.width;
    cvSelected.height = currentTexture.image.height;
    const ctx = cvSelected.getContext('2d');

    ctx.clearRect(0, 0, sz, sz);
    ctx.drawImage(currentTexture.image, 0, 0, cvSelected.width, cvSelected.height);

    const spacing = 6;
    const radius = 0.5;
    for (let y = 0; y < cvSelected.height; y += spacing) {
      for (let x = 0; x < cvSelected.width; x += spacing) {
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const txSelectedRoof = new THREE.CanvasTexture(cvSelected);
    txSelectedRoof.wrapS = txSelectedRoof.wrapT = THREE.RepeatWrapping;
    txSelectedRoof.repeat.set(1, 1);
    txSelectedRoof.needsUpdate = true;

    return txSelectedRoof;
  };

  const setCurrentTexture = (currentTexture, m = -1) => {
    setTexture(currentTexture);
    setSelectedTexture(getSelectedTexture(currentTexture));
    if (m !== -1) setShadingTexture(currentTexture, m);
    changeMaterials(sceneRef.current);
  };

  useEffect(() => {
    if (dsmTexture) dsmTexture.needsUpdate = true;
    changeMaterials(sceneRef.current);
  }, [dsmTexture, sceneRef]);

  const saveTextures = async (info, StorageData, DTSRFData, DTOFData) => {
    const data = materials.saveTextures(info, StorageData, DTSRFData, DTOFData);
    await handleSaveShadings(data);
  };

  const loadShadingJson = async (scr, callback = null) => {
    if (scr) {
      try {
        const res = await fetch(scr);
        if (res.status === 404) {
          changeLoadStatus('Shading.None');
          return null;
        }
        if (!res.ok) throw new Error(`Failed to load shading JSON: ${res.status}`);

        const json = await res.json();
        const { diffuse } = json || {};
        if (!diffuse) throw Error();

        await materials.loadTextures(json);
        if (callback) callback(json);
        changeLoadStatus('Shading.Loaded');
        return json;
      } catch {
        changeLoadStatus('Shading.Failed');
        return null;
      }
    }

    changeLoadStatus('Shading.None');
    return null;
  };

  useLayoutEffect(() => {
    if (!imgSrc) return;

    try {
      new THREE.TextureLoader().load(imgSrc, (tx) => {
        setRGBTexture(tx);
        setCurrentTexture(tx);
        changeLoadStatus('RGB.Loaded');
      });
    } catch {
      console.log('error in load texture');
      changeLoadStatus('RGB.Failed');
    }
  }, [imgSrc]);

  useLayoutEffect(() => {
    try {
      shadingLoadPromiseRef.current = loadShadingJson(proposalShadings, onShadingLoaded);
    } catch (err) {
      console.log(err);
      shadingLoadPromiseRef.current = Promise.resolve(null);
    }
  }, [proposalShadings, onShadingLoaded]);

  const black = useMemo(
    () => (
      <meshStandardMaterial
        attach="material"
        color="rgb(0,0,0)"
        side={THREE.DoubleSide}
        blending={THREE.AdditiveBlending}
        castShadow
        receiveShadow
      />
    ),
    []
  );

  const groundSun = useMemo(
    () => (
      <meshStandardMaterial
        attach="material"
        color="#ffffff"
        wireframe={false}
        side={THREE.DoubleSide}
        map={texture}
        castShadow
        receiveShadow
      />
    ),
    [texture]
  );

  const ground = useMemo(
    () => <meshBasicMaterial attach="material" color="#ffffff" wireframe={false} side={THREE.DoubleSide} map={texture} />,
    [texture]
  );

  const groundDispSun = useMemo(
    () => (
      <meshStandardMaterial
        attach="material"
        color="#ffffff"
        wireframe={false}
        side={THREE.DoubleSide}
        map={texture}
        displacementMap={dsmTexture}
        displacementScale={100}
        castShadow
        receiveShadow
      />
    ),
    [texture, dsmTexture]
  );

  const groundDisp = useMemo(
    () => (
      <meshStandardMaterial
        attach="material"
        color="#000000"
        wireframe={false}
        side={THREE.DoubleSide}
        displacementMap={dsmTexture}
        displacementScale={100}
        emissive="#ffffff"
        emissiveMap={texture}
        metalness={1}
        roughness={1}
      />
    ),
    [texture, dsmTexture]
  );

  const hasSavedProposalShadings = Boolean(proposalShadings);

  // Deprecated compatibility path: when no saved proposal shading artifact is available,
  // keep the legacy texture wait/countdown for older client-generated shading flows.
  // Proposal reloads should pass proposalShadings and use the explicit load promise above
  // instead of polling for baseline textures on a timer.
  const shouldUseLegacyTextureWait = !hasSavedProposalShadings;

  return {
    rgbTexture,
    setRGBTexture,
    texture,
    selectedTexture,
    setTexture,
    setCurrentTexture,
    changeMaterials,
    getAnualPanelsShadings: async (segments, panels, gwidth, gheight, options = {}) => {
      if (hasSavedProposalShadings && options.waitForShadingLoad !== false) {
        await shadingLoadPromiseRef.current;
      }

      return materials.getAnualPanelsShadings(segments, panels, gwidth, gheight, {
        waitForTextures: shouldUseLegacyTextureWait,
        ...options
      });
    },

    resetRGBTexture: () => {
      let randInt = Math.random();
      try {
        new THREE.TextureLoader().load(`${imgSrc}?${randInt}`, (tx) => {
          setRGBTexture(tx);
          setCurrentTexture(tx);
          changeLoadStatus('RGB.Loaded');
        });
      } catch {
        console.log('error in load texture');
        changeLoadStatus('RGB.Failed');
      }
    },

    resetTSRFTextures: () => {
      for (let i = 0; i <= 12; i += 1) materials.clean(i, 'TSRF');
    },
    setTSRFTextures: (m, graySA, gradientSA) => {
      materials.clean(m, 'TSRF');
      materials.TSRFTextures[m] = [graySA, gradientSA];
    },
    getTSRFTexture: (m) => (m in materials.TSRFTextures ? materials.TSRFTextures[m] : null),

    resetTOFTextures: () => {
      for (let i = 0; i <= 12; i += 1) materials.clean(i, 'TOF');
    },
    setTOFTextures: (m, grayTOF, gradientTOF) => {
      materials.clean(m, 'TOF');
      materials.TOFTextures[m] = [grayTOF, gradientTOF];
    },
    getTOFTexture: (m) => (m in materials.TOFTextures ? materials.TOFTextures[m] : null),

    resetSolarAccessTextures: () => {
      for (let i = 0; i <= 12; i += 1) materials.clean(i, 'SA');
    },
    setSolarAccessTextures: (m, graySolarAccess, gradientSolarAccess) => {
      materials.clean(m, 'SA');
      materials.SolarAccessTextures[m] = [graySolarAccess, gradientSolarAccess];
    },
    getSolarAccessTexture: (m) => (m in materials.SolarAccessTextures ? materials.SolarAccessTextures[m] : null),

    getDiffuseTextures: () => [
      materials.diffuseTextures.tsrf_iso,
      materials.diffuseTextures.tof_iso,
      materials.diffuseTextures.tsrf_horizon,
      materials.diffuseTextures.tof_horizon
    ],
    setDiffuseTextures: (diffuseTSRF_iso, diffuseTOF_iso, diffuseTSRF_horizon, diffuseTOF_horizon) => {
      materials.diffuseTextures.tsrf_iso = diffuseTSRF_iso;
      materials.diffuseTextures.tof_iso = diffuseTOF_iso;
      materials.diffuseTextures.tsrf_horizon = diffuseTSRF_horizon;
      materials.diffuseTextures.tof_horizon = diffuseTOF_horizon;
    },

    black,
    groundSun,
    ground,
    groundDispSun,
    groundDisp,

    loadShadingJson,
    saveTextures,
    getMaskCanvas: materials.getMaskCanvas,
    gradiantData: materials.gradiantData
  };
};

export default useMaterials;
